import { zodResolver } from '@hookform/resolvers/zod';
import type { AccountInput, NewAccount } from '@weft/types';
import { NewAccountSchema } from '@weft/types';
import type { PropsWithChildren } from 'react';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useAccount } from '../../../../../../hooks/requests/use-account';
import { useUpdateAccount } from '../../../../../../hooks/requests/use-update-account';
import { runMutation } from '../../../../../../utils/vy-mutation';

export const AccountForm: React.FC<PropsWithChildren> = ({ children }) => {
  const queryClient = useQueryClient();
  const accountQuery = useAccount();
  const updateAccountMutation = useUpdateAccount();

  const methods = useForm<AccountInput, unknown, NewAccount>({
    resolver: zodResolver(NewAccountSchema),
    defaultValues: accountQuery.data?.account,
  });

  const handleSubmit = methods.handleSubmit(async (newAccount) => {
    await runMutation(updateAccountMutation, { account: newAccount }, {
      pending: 'Updating account...',
      success: 'Account updated',
      error: 'Failed to update account',
    });
    await queryClient.invalidateQueries('account');

    methods.reset(newAccount, {
      keepDefaultValues: false,
    });
  });

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={handleSubmit}
        className="h-full w-full"
      >
        {children}
      </form>
    </FormProvider>
  );
};

export const withAccountForm = <ComponentProps extends Record<string, unknown>>(Component: React.ComponentType<ComponentProps>) => {
  return (componentProps: ComponentProps) => {
    return (
      <AccountForm>
        <Component {...componentProps}/>
      </AccountForm>
    );
  };
};